import { schema } from 'normalizr';

import type { MetadataMetadataFieldSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/metadata_field';
import type {
  AssetsApprovalBySchema,
  AssetsApprovalBySchemaNullable,
  AssetsApprovalSchemaNullable,
  AssetsRecentAssetElasticSchemaNullable,
  AssetsRecentCollectionElasticSchemaNullable,
  AssetsRelationTypeSchema,
} from '@iconik-io/iconik-api/src/services/assets';
import type { AuthTokenSchema } from '@iconik-io/iconik-api/src/services/auth';
import type { MlPersonByAssetAndVersionInAdminNullable } from '@iconik-io/iconik-api/src/services/ml';

import type { SearchItemEntity } from './normalized/search_item';
import type { StoreEntityKey } from './types';

function processStrategyUrlSafe<Entity extends { url?: string | null }>(
  item: Entity,
) {
  const { url, ...rest } = item;
  return {
    ...rest,
    // Make sure that null or undefined URL value will not overwrite an existing value.
    // This is important for cases when we already have a signed URL, but got an update
    // without a URL.
    url: url ?? undefined,
  };
}

const metadataFieldEntityOptions: schema.EntityOptions = {
  idAttribute: (item: MetadataMetadataFieldSchemaPatched) => item.name,
  processStrategy: (item: MetadataMetadataFieldSchemaPatched) => ({
    ...item,
    id: item.name,
  }),
};

export const getApprovalId = (
  approval: AssetsApprovalBySchema | AssetsApprovalBySchemaNullable,
) => {
  const versionId = approval.version_id ?? 'unknown';
  const objectId = approval.object_id ?? 'unknown';
  // Either user ID in `user` or email in `external`.
  const user = approval.user ?? approval.external ?? 'unknown';
  return `${objectId}_${versionId}_${user}`;
};

const approvalsSchema = new schema.Entity('approvals', undefined, {
  idAttribute: getApprovalId,
});

export const getApprovalRequestId = ({
  assetId,
  versionId,
}: {
  assetId: string;
  versionId: string;
}) => `${assetId}_${versionId}`;

export const approvalRequestsSchema = new schema.Entity(
  'approval_requests',
  undefined,
  {
    idAttribute: (item: AssetsApprovalSchemaNullable) => {
      if (item.object_id && item.version_id) {
        return getApprovalRequestId({
          assetId: item.object_id,
          versionId: item.version_id,
        });
      }
      throw new Error('object_id or version_id missing');
    },
  },
);

const usersBasicSchema = new schema.Entity('users_basic');

const groupsBasicSchema = new schema.Entity('groups_basic');

const aclTemplatesSchema = new schema.Entity('acl_templates');

const applicationTokensSchema = new schema.Entity(
  'application_tokens',
  undefined,
  {
    idAttribute: (item: AuthTokenSchema) => item.app_id ?? '',
  },
);

const applicationsSchema = new schema.Entity('applications');

const assetPersonsSchema = new schema.Entity('asset_persons');

const assetRelationsSchema = new schema.Entity('asset_relations');

const assetsSchema = new schema.Entity('assets', {
  created_by_user_info: usersBasicSchema,
});

const automationsSchema = new schema.Entity('automations');

const collectionKeyframesSchema = new schema.Entity(
  'collection_keyframes',
  undefined,
  {
    processStrategy: processStrategyUrlSafe,
  },
);

const collectionsSchema = new schema.Entity('collections', {
  created_by_user_info: usersBasicSchema,
});

const customActionsSchema = new schema.Entity('custom_actions');

const discoveriesSchema = new schema.Entity('discoveries');

const discoveriesAdminSchema = new schema.Entity('discoveries_admin');

const exportLocationsSchema = new schema.Entity('export_locations');

const fileSetsSchema = new schema.Entity('file_sets');

const filesSchema = new schema.Entity('files', undefined, {
  processStrategy: processStrategyUrlSafe,
});

const formatsSchema = new schema.Entity('formats');

const groupsSchema = new schema.Entity('groups');

const identityProvidersSchema = new schema.Entity('identity_providers');

const jobsSchema = new schema.Entity('jobs');

const keyframesSchema = new schema.Entity('keyframes', undefined, {
  processStrategy: processStrategyUrlSafe,
});

const metadataFieldsSchema = new schema.Entity(
  'metadata_fields',
  undefined,
  metadataFieldEntityOptions,
);

const metadataUnmergedViewsSchema = new schema.Entity(
  'metadata_unmerged_views',
);

const metadataUserFieldsSchema = new schema.Entity(
  'metadata_user_fields',
  undefined,
  metadataFieldEntityOptions,
);

const metadataViewsSchema = new schema.Entity('metadata_views');

const searchViewsSchema = new schema.Entity('search_views');

const notificationsSchema = new schema.Entity('notifications');

const personInstancesSchema = new schema.Entity('person_instances', undefined, {
  idAttribute: (item: MlPersonByAssetAndVersionInAdminNullable) =>
    item.person_version_face_id,
});

const personsSchema = new schema.Entity('persons');

const playlistsSchema = new schema.Entity('playlists');

const portalsSchema = new schema.Entity('portals');

const projectsSchema = new schema.Entity('projects');

const proxiesSchema = new schema.Entity('proxies', undefined, {
  processStrategy: processStrategyUrlSafe,
});

const recycleBinFileSetsSchema = new schema.Entity('recycle_bin_file_sets');

const recycleBinFormatsSchema = new schema.Entity('recycle_bin_formats');

const relationTypesSchema = new schema.Entity('relation_types', undefined, {
  idAttribute: (item: AssetsRelationTypeSchema) => item.name,
  processStrategy: (item: AssetsRelationTypeSchema) => ({
    id: item.name,
    ...item,
  }),
});

const roleGroupsSchema = new schema.Entity('role_groups');

const roleGroupsBasicSchema = new schema.Entity('role_groups_basic');

const savedSearchResultsSchema = new schema.Entity('saved_search_results');

const savedSearchesSchema = new schema.Entity('saved_searches');

const searchBackendsSchema = new schema.Entity('search_backends');

const searchHistorySchema = new schema.Entity('search_history');

const searchItemsSchema = new schema.Entity(
  'search_items',
  {
    proxies: [proxiesSchema],
    files: [filesSchema],
    formats: [formatsSchema],
  },
  {
    processStrategy: (item: SearchItemEntity) => {
      if (item.type === 'SUBCLIP') {
        // In subclips it's impossible to set proper asset_id and version_id,
        // so their nested documents can't be properly indexed.
        // We still require this data to work with normalization/denormalization,
        // so we skip processing for subclips.
        return item;
      }

      const latestActiveVersionId = item.versions?.findLast(
        (version) => version.status === 'ACTIVE',
      )?.id;

      return {
        ...item,
        proxies: item.proxies?.map((proxy) => ({
          ...proxy,
          asset_id: item.id,
          version_id: latestActiveVersionId,
        })),
        files: item.files?.map((file) => ({
          ...file,
          asset_id: item.id,
          version_id: latestActiveVersionId,
        })),
        formats: item.formats?.map((format) => ({
          ...format,
          asset_id: item.id,
          version_id: latestActiveVersionId,
        })),
      };
    },
  },
);

const segmentsSchema = new schema.Entity('segments', {
  user_info: usersBasicSchema,
});

const shareUsersSchema = new schema.Entity('share_users');

const sharesSchema = new schema.Entity('shares');

const storageGatewaysSchema = new schema.Entity('storage_gateways');

const storageGatewayClustersSchema = new schema.Entity(
  'storage_gateway_clusters',
  {
    nodes: [storageGatewaysSchema],
  },
);

const storagesSchema = new schema.Entity('storages');

const subtitlesSchema = new schema.Entity('subtitles');

const systemLogsRecipientsSchema = new schema.Entity('system_logs_recipients');

const systemDomainsSchema = new schema.Entity('system_domains');

const teamsSchema = new schema.Entity('teams');

const teamsBasicSchema = new schema.Entity('teams_basic');

const logosSchema = new schema.Entity('logos');

const themesSchema = new schema.Entity('themes');

const systemDomainsBasicSchema = new schema.Entity('system_domains_basic');

const transcodersSchema = new schema.Entity('transcoders');

const transcriptionPropertiesSchema = new schema.Entity(
  'transcription_properties',
);

const usersSchema = new schema.Entity('users');

const recentSearchItemsSchema = new schema.Entity(
  'recent_search_items',
  undefined,
  {
    idAttribute: (
      item:
        | AssetsRecentAssetElasticSchemaNullable
        | AssetsRecentCollectionElasticSchemaNullable,
    ) => `${item.object_type}_${item.object_id}`,
  },
);

const webhooksSchema = new schema.Entity('webhooks');

export const entitySchemas: Record<StoreEntityKey, schema.Entity> = {
  acl_templates: aclTemplatesSchema,
  application_tokens: applicationTokensSchema,
  applications: applicationsSchema,
  asset_persons: assetPersonsSchema,
  asset_relations: assetRelationsSchema,
  assets: assetsSchema,
  approvals: approvalsSchema,
  approval_requests: approvalRequestsSchema,
  automations: automationsSchema,
  collection_keyframes: collectionKeyframesSchema,
  collections: collectionsSchema,
  custom_actions: customActionsSchema,
  discoveries: discoveriesSchema,
  discoveries_admin: discoveriesAdminSchema,
  export_locations: exportLocationsSchema,
  file_sets: fileSetsSchema,
  files: filesSchema,
  formats: formatsSchema,
  groups: groupsSchema,
  groups_basic: groupsBasicSchema,
  identity_providers: identityProvidersSchema,
  jobs: jobsSchema,
  keyframes: keyframesSchema,
  logos: logosSchema,
  metadata_fields: metadataFieldsSchema,
  metadata_unmerged_views: metadataUnmergedViewsSchema,
  metadata_user_fields: metadataUserFieldsSchema,
  metadata_views: metadataViewsSchema,
  search_views: searchViewsSchema,
  notifications: notificationsSchema,
  person_instances: personInstancesSchema,
  persons: personsSchema,
  playlists: playlistsSchema,
  portals: portalsSchema,
  projects: projectsSchema,
  proxies: proxiesSchema,
  recycle_bin_file_sets: recycleBinFileSetsSchema,
  recycle_bin_formats: recycleBinFormatsSchema,
  relation_types: relationTypesSchema,
  role_groups: roleGroupsSchema,
  role_groups_basic: roleGroupsBasicSchema,
  saved_search_results: savedSearchResultsSchema,
  saved_searches: savedSearchesSchema,
  search_backends: searchBackendsSchema,
  search_history: searchHistorySchema,
  search_items: searchItemsSchema,
  segments: segmentsSchema,
  share_users: shareUsersSchema,
  shares: sharesSchema,
  storage_gateways: storageGatewaysSchema,
  storage_gateway_clusters: storageGatewayClustersSchema,
  storages: storagesSchema,
  subtitles: subtitlesSchema,
  system_domains: systemDomainsSchema,
  system_domains_basic: systemDomainsBasicSchema,
  system_logs_recipients: systemLogsRecipientsSchema,
  teams: teamsSchema,
  teams_basic: teamsBasicSchema,
  themes: themesSchema,
  transcoders: transcodersSchema,
  transcription_properties: transcriptionPropertiesSchema,
  users: usersSchema,
  users_basic: usersBasicSchema,
  recent_search_items: recentSearchItemsSchema,
  webhooks: webhooksSchema,
};

export const isStoreEntityKey = (key: string): key is StoreEntityKey =>
  Object.hasOwn(entitySchemas, key);
